import React from 'react';
import Dropzone from "react-dropzone";
import loadImage from "blueimp-load-image";
import PropTypes from "prop-types";

import * as notificationAction from "../../shared/modules/notification/actions/notificationsAction";
import {MAX_PICTURE_HEIGHT, MAX_PICTURE_WIDTH} from "../constants/image";
import {PLATFORM_ANDROID, ANALYTICS_SPECS} from "../constants";
import {NOTIFICATION_ERROR} from "../../shared/modules/notification/constants";
import {isCordova} from "../../app";
import {sendAnalyticsEvent} from '../actions/analytics';
import { Plugins, CameraResultType, CameraSource } from '@capacitor/core';

const { Camera } = Plugins;

export default class ImageUploader extends React.Component {

  static propTypes = {
    imagePreviewUrl: PropTypes.string,
    analyticsCategory: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
  };

  static defaultProps = {imagePreviewUrl: ''};

  state = {
    errors: {},
    isLoading: false
  };

  discardImage() {
    this.setState({
      errors: {},
      isLoading: false
    });
    this.props.onChange({ file: null, url: '' });
  }

  onRejectImage = () => {
    this.props.dispatch(
      notificationAction.addNotification(
        __("The allowed formats are png and jpg/jpeg."),
        NOTIFICATION_ERROR
      ));
  };

  onAcceptImage = ([file]) => {
    loadImage.parseMetaData(file, (data) => {
      // Default image orientation
      let orientation = 0;

      // If exif data available, update orientation
      if (data.exif) {
        orientation = data.exif.get("Orientation");
        console.log(orientation);
      } else {
        console.log("No exif data!");
      }
      // Loading the image to dropzone
      loadImage(
        file,
        (canvas) => {
          if (canvas.width > MAX_PICTURE_WIDTH || canvas.height > MAX_PICTURE_HEIGHT) {
            new Promise(
              (resolve) => {
                let {width, height} = canvas;
                const ratio = canvas.width / canvas.height;

                if (canvas.width > MAX_PICTURE_WIDTH && canvas.height > MAX_PICTURE_HEIGHT) {
                  if (ratio > (16 / 9)) {
                    width = MAX_PICTURE_WIDTH;
                    height = width / ratio;
                  } else {
                    height = MAX_PICTURE_HEIGHT;
                    width = height * ratio;
                  }
                } else if (canvas.height > MAX_PICTURE_HEIGHT) {
                  height = MAX_PICTURE_HEIGHT;
                  width = height * ratio;
                } else if (canvas.width > MAX_PICTURE_WIDTH) {
                  width = MAX_PICTURE_WIDTH;
                  height = width * ratio;
                }
                resolve([Math.round(width), Math.round(height)]);
              })
              .then((dimensions) => {
                const newCanvas = document.createElement('canvas');
                const [width, height] = dimensions;

                newCanvas.width = width;
                newCanvas.height = height;
                const context = newCanvas.getContext('2d');

                context.drawImage(canvas, 0, 0, dimensions[0], dimensions[1]);
                return newCanvas;
              })
              .then((newcanvas) => {
                const blob = this.convertBase64ToBlob(newcanvas.toDataURL());

                blob.lastModified = file.lastModified;
                blob.lastModifiedDate = file.lastModifiedDate;
                blob.name = file.name;
                this.props.onChange({
                  file: blob,
                  url: newcanvas.toDataURL()
                });
              });
          } else {
            this.props.onChange({
              file: file,
              url: canvas.toDataURL()
            });
          }
        },
        {
          // Should be set to canvas : true to activate auto fix orientation
          canvas: true,
          orientation: orientation
        }
      );
    });

    if (this.props.analyticsCategory === ANALYTICS_SPECS.DIARY.NAME) {
      sendAnalyticsEvent(ANALYTICS_SPECS.DIARY.NAME, ANALYTICS_SPECS.DIARY.EVENTS.SELECT_PICTURE);
    } else if (this.props.analyticsCategory === ANALYTICS_SPECS.MESSAGING.NAME) {
      sendAnalyticsEvent(ANALYTICS_SPECS.MESSAGING.NAME, ANALYTICS_SPECS.MESSAGING.EVENTS.SELECT_PICTURE);
    }

  };

  captureImage = () => {
    // Set Using Camera True
    document.isUsingCamera = true;
    Camera.getPhoto({
      quality: 70,
      resultType: CameraResultType.DataUrl,
      correctOrientation: true,
      saveToGallery: true,
      source: CameraSource.Camera,
      height: 800,
      width: 800
    })
    .then(this.captureSuccess)
    .catch(console.err)
    .finally(() => {
      document.isUsingCamera = false;
    });

    if (this.props.analyticsCategory === ANALYTICS_SPECS.DIARY.NAME) {
      sendAnalyticsEvent(ANALYTICS_SPECS.DIARY.NAME, ANALYTICS_SPECS.DIARY.EVENTS.TAKE_PICTURE);
    } else if (this.props.analyticsCategory === ANALYTICS_SPECS.MESSAGING.NAME) {
      sendAnalyticsEvent(ANALYTICS_SPECS.MESSAGING.NAME, ANALYTICS_SPECS.MESSAGING.EVENTS.TAKE_PICTURE);
    }
  };

  captureSuccess = ({ dataUrl }) => {
    this.props.onChange({
      file: this.b64toBlob(dataUrl.split(',')[1], "image/jpeg"),
      url: dataUrl
    });
  };

  convertBase64ToBlob(dataURI) {
    // Convert base64/URLEncoded data component to raw binary data held in a string
    let byteString = '';

    if (dataURI.split(',')[0].indexOf('base64') >= 0) {
      byteString = atob(dataURI.split(',')[1]);
    } else {
      byteString = unescape(dataURI.split(',')[1]);
    }
    // Separate out the mime component
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];

    // Write the bytes of the string to a typed array
    const ia = new Uint8Array(byteString.length);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ia], {type: mimeString});
  }

  b64toBlob(b64Data, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);

      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      byteArrays.push(new Uint8Array(byteNumbers));
    }

    const blob = new Blob(byteArrays, {type: contentType});

    blob.name = (new Date()).getTime();
    return blob;
  }

  render() {
    const { isLoading, errors } = this.state;
    const { imagePreviewUrl } = this.props;

    return (
      <div id="picture">
        {!imagePreviewUrl && <div className="uploadPlaceholder"/>}
        {isCordova() && cordova.platformId === PLATFORM_ANDROID &&
          <button
            className="buttonPrimary"
            style={{display: imagePreviewUrl ? "none" : "block"}}
            onClick={this.captureImage}
          >
            <i className="fa fa-camera"/>
            {__("Take a picture")}
          </button>}
        <Dropzone
          className="buttonPrimary"
          style={{display: imagePreviewUrl ? "none" : "block"}}
          multiple={false}
          accept="image/jpeg, image/png"
          onDropRejected={this.onRejectImage}
          onDropAccepted={this.onAcceptImage}
        >
          <i className="fa fa-folder"/>
          {__("Choose a picture")}
        </Dropzone>
        {isLoading &&
        <div className="loading">
          <b>
            <i className="fa fa-spinner fa-spin fa-2x fa-fw"/>
            <span className="sr-only">
              {__("Picture is being uploaded, please wait.")}
          </span>
          </b>
        </div>}
        {errors.file &&
        <div className="has-error">
          {errors.file}
        </div>}
        {imagePreviewUrl && <div
          className="imageUpload"
          style={{
            backgroundImage: `url(${imagePreviewUrl.replace(/\n/g, '')})`,
            display: "block"
          }}
        >
          <button className="buttonClose"
                  onClick={() => this.discardImage()}>
            <i className="fa fa-times"/>
          </button>
        </div>}
      </div>
    );
  }
}
